import { camelCase } from "lodash";
import { useTranslation } from "react-i18next";

import EditablePreviewFieldRequest from "@krea/customer-features/shared-components/field-request/EditablePreviewFieldRequest";
import { useGetCachedCurrentLoanApplication } from "@krea/customer-features/store/current-loan-application/hooks";
import { useGetFieldRequests } from "@krea/customer-features/store/field-requests/hooks/queries";
import { FieldRequestFieldEnum } from "@krea/customer-features/types/fieldRequest";
import { AttractivenessImprovementTypeEnum } from "@krea/customer-features/types/loanApplication";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@krea/shared/design-system/accordion";

export const Improvements = () => {
  const { t } = useTranslation();

  const { data: currentLoanApplication } = useGetCachedCurrentLoanApplication();
  const { data: fieldRequests = [] } = useGetFieldRequests();

  const { applicationImprovements = [] } = currentLoanApplication ?? {};

  const getFieldRequestId = (improvementField: string) => {
    const id = fieldRequests.find(
      (fieldRequest) =>
        fieldRequest.field ===
        (camelCase(improvementField) as FieldRequestFieldEnum),
    )?.id;

    return id;
  };

  // group improvements by type

  const groupedImprovements = applicationImprovements.reduce<
    Record<AttractivenessImprovementTypeEnum, typeof applicationImprovements>
  >(
    (acc, improvement) => {
      const type = improvement.type;

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!acc[type]) {
        acc[type] = [];
      }

      acc[type].push(improvement);

      return acc;
    },
    // eslint-disable-next-line @typescript-eslint/prefer-reduce-type-parameter
    {} as Record<
      AttractivenessImprovementTypeEnum,
      typeof applicationImprovements
    >,
  );

  return (
    <Accordion type="multiple" className="tw-flex tw-flex-col tw-gap-3">
      {Object.entries(groupedImprovements).map(([type, improvements]) => {
        return (
          <AccordionItem
            key={type}
            value={type}
            className="tw-border tw-rounded-lg tw-border-primary-200 tw-pl-3 tw-pr-2"
          >
            <AccordionTrigger className="tw-font-semibold tw-text-sm lg:tw-text-md tw-py-2">
              {improvements[0].displayText}
            </AccordionTrigger>
            <AccordionContent>
              {improvements.map((improvement) => {
                const id = getFieldRequestId(improvement.field);

                return (
                  <EditablePreviewFieldRequest
                    key={improvement.field + (id ?? "")}
                    fieldRequestID={id}
                    preview={improvements.length > 1}
                  />
                );
              })}
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
};

export default Improvements;
